import { fail } from './errors.js';

/*
 * 급상여대장·임금명세서.
 * - 원장이 직원별로 입력하고, 4대 보험과 지방소득세는 자동 계산한 뒤 고칠 수 있다.
 * - 소득세는 간이세액표를 따르므로 입력한다.
 * - 저장할 때 항목 전체를 암호화한다. 발행 전에는 원장만, 발행 뒤에는 본인이 자기 명세서만 본다.
 * - 근로기준법 제48조의 기재 사항(성명·생년월일 또는 사번·지급일·임금 총액·항목별 금액·계산 방법·공제 내역·
 *   근로일수·총 근로시간·연장/야간/휴일 시간)을 명세서에 담는다.
 */

export const EARNING_ITEMS = [
  ['base', '기본급'], ['bonus', '상여'], ['position', '직책수당'], ['leave', '월차수당'],
  ['meal', '식대'], ['car', '자가운전보조금'], ['night', '야간근로수당'], ['incentive', '인센티브'],
] as const;
export type EarningKey = (typeof EARNING_ITEMS)[number][0];

export const DEDUCTION_ITEMS = [
  ['pension', '국민연금'], ['health', '건강보험'], ['care', '장기요양보험'], ['employment', '고용보험'],
  ['pensionSupport', '국민연금지원'], ['employmentSupport', '고용보험지원'], ['incomeTax', '소득세'], ['localTax', '지방소득세'],
  ['ruralTax', '농특세'], ['yearEndTax', '연말정산소득세'], ['yearEndLocalTax', '연말정산지방소득세'], ['other', '기타공제'],
] as const;
export type DeductionKey = (typeof DEDUCTION_ITEMS)[number][0];

export const EMPLOYMENT_LABEL = { REGULAR: '정규직', CONTRACT: '계약직' } as const;
export type EmploymentType = keyof typeof EMPLOYMENT_LABEL;

/** 해마다 바뀌는 값. 고시가 바뀌면 여기를 고친다. */
export const PAYROLL_RATES = { year: 2026, pension: 0.0475, health: 0.03595, careOfHealth: 0.1314, employment: 0.009, localOfIncomeTax: 0.1, minHourlyWage: 10_320 } as const;
/** 월 비과세 한도(소득세법 시행령): 식대, 자가운전보조금 */
export const NONTAX_LIMIT = { meal: 200_000, car: 200_000 } as const;
/** 주 평균 주 수 */
export const WEEKS_PER_MONTH = 4.345;

const MONEY_MAX = 100_000_000;
const TEXT_MAX = 60;

export interface PayrollLine {
  userId: string;
  name: string;
  employeeNo: string;    // 사번
  birthDate: string;     // YYYY-MM-DD
  email: string;         // 발행 알림을 받을 주소
  hireDate: string;
  department: string;
  position: string;
  employment: EmploymentType;
  family: number;
  weeklyHours: number;   // 소정근로시간(주휴 포함 계산의 기준)
  hourlyWage: number;
  workDays: number;
  workHours: number;
  overtimeHours: number;
  nightHours: number;
  holidayHours: number;
  pensionBase: number;   // 기준소득월액(신고한 값)
  earnings: Record<EarningKey, number>;
  deductions: Record<DeductionKey, number>;
}

export type PayrollStatus = 'DRAFT' | 'ISSUED';

/** 저장 형태: 직원별 내용은 linesEnc 에 암호화해 둔다. */
export interface PayrollBook {
  centerId: string;
  month: string;
  payDate: string;
  status: PayrollStatus;
  linesEnc: string;
  headcount: number;
  version: number;
  updatedAt: string;
  updatedBy: string;
  issuedAt?: string;
}

const zeros = <K extends string>(items: ReadonlyArray<readonly [K, string]>) => Object.fromEntries(items.map(([k]) => [k, 0])) as Record<K, number>;

export function emptyPayrollLine(i: { userId: string; name: string; position?: string }): PayrollLine {
  return {
    userId: i.userId, name: i.name, employeeNo: '', birthDate: '', email: '', hireDate: '', department: '', position: i.position ?? '', employment: 'REGULAR', family: 1,
    weeklyHours: 0, hourlyWage: 0, workDays: 0, workHours: 0, overtimeHours: 0, nightHours: 0, holidayHours: 0, pensionBase: 0,
    earnings: zeros(EARNING_ITEMS), deductions: zeros(DEDUCTION_ITEMS),
  };
}

/** 지난달 인적 사항과 고정 지급액을 이어받는다. 근로시간과 변동 항목은 새로 입력한다. */
export function carryPayrollLine(prev: PayrollLine): PayrollLine {
  return {
    ...prev, workDays: 0, workHours: 0, overtimeHours: 0, nightHours: 0, holidayHours: 0,
    earnings: { ...zeros(EARNING_ITEMS), base: prev.earnings.base, position: prev.earnings.position, meal: prev.earnings.meal, car: prev.earnings.car },
    deductions: zeros(DEDUCTION_ITEMS),
  };
}

const sum = (v: Record<string, number>) => Object.values(v).reduce((a, b) => a + b, 0);
/** 4대 보험료와 지방소득세는 10원 미만을 버린다. */
const floor10 = (n: number) => Math.floor(Math.round(n * 1000) / 10000) * 10;

export const payrollGross = (l: Pick<PayrollLine, 'earnings'>) => sum(l.earnings);
export const payrollDeduction = (l: Pick<PayrollLine, 'deductions'>) => sum(l.deductions);
export const payrollNet = (l: Pick<PayrollLine, 'earnings' | 'deductions'>) => payrollGross(l) - payrollDeduction(l);
/** 보수월액: 지급 합계에서 비과세 식대·자가운전보조금을 뺀 금액 */
export const payrollTaxable = (l: Pick<PayrollLine, 'earnings'>) =>
  payrollGross(l) - Math.min(l.earnings.meal, NONTAX_LIMIT.meal) - Math.min(l.earnings.car, NONTAX_LIMIT.car);

/** 월 소정근로시간: 주 시간 × 4.345 (소수 첫째 자리) */
export const monthlyHours = (weeklyHours: number) => Math.round(weeklyHours * WEEKS_PER_MONTH * 10) / 10;

/** 자동 계산: 기본급(시급·주 시간이 있을 때), 4대 보험, 지방소득세. 소득세·기타공제는 입력값을 둔다. */
export function calcPayrollLine(l: PayrollLine): PayrollLine {
  const base = l.hourlyWage && l.weeklyHours ? Math.round(monthlyHours(l.weeklyHours) * l.hourlyWage) : l.earnings.base;
  const earnings = { ...l.earnings, base };
  const taxable = payrollTaxable({ earnings });
  const health = floor10(taxable * PAYROLL_RATES.health);
  return {
    ...l, earnings,
    deductions: {
      ...l.deductions,
      pension: floor10(l.pensionBase * PAYROLL_RATES.pension), health, care: floor10(health * PAYROLL_RATES.careOfHealth),
      employment: floor10(taxable * PAYROLL_RATES.employment), localTax: floor10(l.deductions.incomeTax * PAYROLL_RATES.localOfIncomeTax),
    },
  };
}

const nf = (n: number) => n.toLocaleString('ko-KR');
const pct = (r: number) => `${Math.round(r * 100000) / 1000}%`;

/** 명세서의 계산 방법. 금액이 있는 항목만 싣는다. */
export function payrollMethods(l: PayrollLine): Array<{ item: string; method: string; amount: number }> {
  const taxable = payrollTaxable(l);
  const rows = [
    { item: '기본급', amount: l.earnings.base, method: l.hourlyWage && l.weeklyHours ? `주 ${l.weeklyHours}시간 × ${WEEKS_PER_MONTH} = 월 ${monthlyHours(l.weeklyHours)}시간, ${monthlyHours(l.weeklyHours)}시간 × ${nf(l.hourlyWage)}원 (주휴 포함)` : '근로계약서에 정한 금액' },
    { item: '국민연금', amount: l.deductions.pension, method: `기준소득월액 ${nf(l.pensionBase)}원 × ${pct(PAYROLL_RATES.pension)}` },
    { item: '건강보험', amount: l.deductions.health, method: `보수월액 ${nf(taxable)}원 × ${pct(PAYROLL_RATES.health)}` },
    { item: '장기요양보험', amount: l.deductions.care, method: `건강보험료 × ${pct(PAYROLL_RATES.careOfHealth)}` },
    { item: '고용보험', amount: l.deductions.employment, method: `보수월액 ${nf(taxable)}원 × ${pct(PAYROLL_RATES.employment)}` },
    { item: '소득세', amount: l.deductions.incomeTax, method: '근로소득 간이세액표' },
    { item: '지방소득세', amount: l.deductions.localTax, method: `소득세 × ${pct(PAYROLL_RATES.localOfIncomeTax)}` },
  ];
  return rows.filter((r) => r.amount > 0);
}

const DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const text = (v: unknown, label: string, max = TEXT_MAX) => {
  const s = typeof v === 'string' ? v.trim() : '';
  if (s.length > max) fail('VALIDATION', `${label}은(는) ${max}자 이하로 입력하세요.`);
  return s;
};
const date = (v: unknown, label: string) => {
  const s = text(v, label, 10);
  if (s && !DATE_RE.test(s)) fail('VALIDATION', `${label}을(를) YYYY-MM-DD 형식으로 입력하세요.`);
  return s;
};
const amount = (v: unknown, label: string) => {
  const n = Number(v ?? 0);
  if (!Number.isInteger(n) || n < 0 || n > MONEY_MAX) fail('VALIDATION', `${label} 금액을 확인하세요.`);
  return n;
};
const hours = (v: unknown, label: string) => {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n) || n < 0 || n > 744) fail('VALIDATION', `${label}을(를) 확인하세요.`);
  return n;
};
const record = <K extends string>(items: ReadonlyArray<readonly [K, string]>, v: Record<string, unknown> | undefined) =>
  Object.fromEntries(items.map(([k, label]) => [k, amount(v?.[k], label)])) as Record<K, number>;

/** 입력 검증·정리. 이름은 직원 명부에서 가져온 값을 쓴다. */
export function normalizePayrollLine(i: Partial<PayrollLine>, staff: { userId: string; name: string }): PayrollLine {
  const email = text(i.email, '이메일', 100);
  if (email && !EMAIL_RE.test(email)) fail('VALIDATION', `${staff.name} 선생님의 이메일 형식을 확인하세요.`);
  return {
    userId: staff.userId, name: staff.name, employeeNo: text(i.employeeNo, '사번', 20), birthDate: date(i.birthDate, '생년월일'), email, hireDate: date(i.hireDate, '입사일'),
    department: text(i.department, '부서'), position: text(i.position, '직위'), employment: i.employment === 'CONTRACT' ? 'CONTRACT' : 'REGULAR',
    family: Math.min(20, Math.max(1, Math.floor(hours(i.family ?? 1, '가족 수')))), weeklyHours: hours(i.weeklyHours, '주 근로시간'), hourlyWage: amount(i.hourlyWage, '시급'),
    workDays: Math.min(31, hours(i.workDays, '근로일수')), workHours: hours(i.workHours, '총 근로시간'), overtimeHours: hours(i.overtimeHours, '연장근로시간'),
    nightHours: hours(i.nightHours, '야간근로시간'), holidayHours: hours(i.holidayHours, '휴일근로시간'), pensionBase: amount(i.pensionBase, '기준소득월액'),
    earnings: record(EARNING_ITEMS, i.earnings), deductions: record(DEDUCTION_ITEMS, i.deductions),
  };
}

/** 이번 달에 지급이 있는 직원. 지급액이 0인 사람에게는 명세서를 내지 않는다. */
export const paidLines = (lines: readonly PayrollLine[]) => lines.filter((l) => payrollGross(l) > 0);

/** 발행 전 확인: 임금명세서에 꼭 적어야 하는 값이 비어 있으면 막는다. */
export function requireIssuable(lines: readonly PayrollLine[], payDate: string) {
  if (!DATE_RE.test(payDate)) fail('VALIDATION', '지급일을 입력하세요.');
  if (!paidLines(lines).length) fail('VALIDATION', '지급액을 입력한 직원이 없습니다.');
  for (const l of paidLines(lines)) {
    if (!l.birthDate && !l.employeeNo) fail('VALIDATION', `${l.name}: 생년월일이나 사번을 입력하세요.`);
    if (!l.workDays || !l.workHours) fail('VALIDATION', `${l.name}: 근로일수와 총 근로시간을 입력하세요.`);
    if (payrollNet(l) < 0) fail('VALIDATION', `${l.name}: 공제액이 지급액보다 큽니다.`);
  }
}

/** 발행 알림 메일. 금액은 넣지 않고 로그인해서 보게 한다. */
export function payrollNoticeMail(i: { month: string; centerName: string; link: string }) {
  const [y, m] = i.month.split('-');
  const title = `${y}년 ${Number(m)}월분 임금명세서`;
  return {
    subject: `[${i.centerName}] ${title}가 발행되었습니다`,
    text: `${i.centerName}에서 ${title}를 발행했습니다.\n\n개인정보 보호를 위해 금액은 메일에 적지 않습니다. 아래 주소에서 로그인해 확인해 주세요.\n${i.link}\n`,
  };
}
